import { memo, useState } from "react";

import { X, Plus } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface ProjectTagsProps {
  projectId: string;
  projectTags: Tag[];
  availableTags: Tag[];
  onRefresh: () => void;
}

const ProjectTagsComponent = ({
  projectId,
  projectTags,
  availableTags,
  onRefresh,
}: ProjectTagsProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleAddTag = async (tagId: string) => {
    try {
      const { error } = await supabase.from("project_tags").insert({
        project_id: projectId,
        tag_id: tagId,
      });

      if (error) throw error;

      toast.success("Tag added");
      onRefresh();
    } catch (error: any) {
      if (error.code === "23505") {
        toast.error("Tag already added");
      } else {
        toast.error(error.message || "Failed to add tag");
      }
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    try {
      const { error } = await supabase
        .from("project_tags")
        .delete()
        .eq("project_id", projectId)
        .eq("tag_id", tagId);

      if (error) throw error;

      toast.success("Tag removed");
      onRefresh();
    } catch (error: any) {
      toast.error(error.message || "Failed to remove tag");
    }
  };

  const unassignedTags = availableTags.filter(
    (tag) => !projectTags.find((pt) => pt.id === tag.id)
  );

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {projectTags.map((tag) => (
        <Badge
          key={tag.id}
          style={{
            backgroundColor: tag.color,
            color: "#ffffff",
          }}
          className="gap-1"
        >
          {tag.name}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveTag(tag.id);
            }}
            className="hover:bg-white/20 rounded-full p-0.5"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}

      {unassignedTags.length > 0 && (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-6">
              <Plus className="h-3 w-3 mr-1" />
              Add Tag
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Add Tag</h4>
              {unassignedTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => {
                    handleAddTag(tag.id);
                    setIsOpen(false);
                  }}
                  className="w-full text-left"
                >
                  <Badge
                    style={{
                      backgroundColor: tag.color,
                      color: "#ffffff",
                    }}
                    className="w-full justify-start"
                  >
                    {tag.name}
                  </Badge>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export const ProjectTags = memo(ProjectTagsComponent);
